import React from 'react';
import { Navigate, NavLink } from 'react-router-dom';
import { Server } from '../../server/server';
import MessageModal from '../modals/MessageModal';
import AlertModal from '../modals/AlertModal';
import PostCache from '../util/PostCache';
import PageEnvelope from '../elements/PageEnvelope';
import PageHeader from '../elements/PageHeader';
import PostPanel from '../elements/PostPanel';
import ContentPanel from '../elements/ContentPanel';
import QuestionModal from '../modals/QuestionModal';
import ConvertToForumModal from '../modals/ConvertToForumModal';
import ActivityButtons from '../elements/ActivityButtons';
import { BsCaretDownFill, BsCaretUpFill } from 'react-icons/bs';
import { HiArrowUturnDown } from 'react-icons/hi2';
import './SitePage.css'
import 'react-quill/dist/quill.snow.css';

interface ActivityPostPageState {
  post: any;
  message: string;
  alert: string;
  question: string;
  onQuestionYes: Function;
  loading: boolean;
  missing: boolean;
  date: string;
  navigate: string;
  sort: boolean;
  editReply: string;
  editContent: boolean;
  loadingMore: boolean;
  replies: any[];
  more: any;
  showConvertModal: boolean;
}

class ActivityPostPage extends React.Component<{}, ActivityPostPageState> {
  constructor(props: {}) {
    super(props);
    this.state = {
      post: '',
      message: '',
      alert: '',
      question: '',
      onQuestionYes: null,
      loading: true,
      missing: false,
      date: '',
      navigate: '',
      sort: true,
      editReply: '',
      editContent: false,
      loadingMore: false,
      replies: [],
      more: null,
      showConvertModal: false
    };
  }

  async componentDidMount() {
    // this.loadPage();
    this.loadPost();
  }

  getCacheKey() {
    let id = window.location.pathname.split('/')[3];
    return `activity-${id}`;
  }

  async loadPost() {
    let id = window.location.pathname.split('/')[3];

    let post = await Server.activity.getPostFromCache(id);

    // get blog from server
    if(!post) {
      let response:any = await Server.activity.getPost(id);

      if (!response.success) {
        this.setState({alert: response.message});
        return;
      }

      post = response.post;

      await Server.activity.setPostInCache(post);
    }

    await Server.public.loadProfiles([post.author]);

    await this.loadReplies(post.id, this.state.sort);

    Server.monitor.setActivityLastRead(post.date)

    this.setState({post});
  }

  async loadReplies(postId:string, sort:boolean) {
    let cache = await Server.activity.getPostRepliesFromCache(postId, sort);
    
    let updating = false;
    if (cache) {
      let profiles = this.getProfilesInReplies(cache.replies);
      profiles = profiles.filter((profile:any)=>Server.public.getProfile(profile) == null);
      if(profiles.length > 0) {
        this.setState({loading: true});
        await Server.public.loadProfiles(profiles);
      }
  
      this.setState({replies: cache.replies, more: cache.more});

      if(sort && cache.more) {
        this.setState({loading: false});
        return;
      }

      updating = cache.replies.length > 0;
      let refreshTimeItem = window.sessionStorage.getItem(`ActivityPostReplyRefresh#${postId}#${sort}`);
      if(refreshTimeItem) {
        let refreshTime = parseInt(refreshTimeItem);
        let now = new Date().getTime();
        if(now - refreshTime < 1000 * 60) {
          this.setState({loading: false});
          return;
        }
      }
    }

    let params:any = {id: postId, sort};
    if(updating) {
      let pi = sort ? cache.replies.length-1 : 1;
      params.after = {date: cache.replies[pi].date, user: cache.replies[pi].user};
    }

    let response = await Server.activity.getPostReplies(params);

    if (!response.success) {
      this.setState({alert: response.message, loading: false});
      return;
    }

    window.sessionStorage.setItem(`ActivityPostReplyRefresh#${postId}#${sort}`, new Date().getTime().toString());

    let newReplies = response.replies;
    let more = response.more;

    let profiles = this.getProfilesInReplies(newReplies);
    await Server.public.loadProfiles(profiles);

    let replies = [];
    if(updating) 
      replies = this.addNewRepliesToExistingReplies(newReplies, this.state.replies, sort);
    else  
      replies = newReplies;

    await Server.activity.setPostRepliesInCache(postId, sort, {replies, more});

    this.setState({replies, more, loading: false});
  }

  async loadMoreReplies() {
    this.setState({loadingMore: true});

    let response = await Server.activity.getPostReplies({id: this.state.post.id, sort: this.state.sort, more: this.state.more});

    if(!response.success) {
      this.setState({loadingMore: false, alert: response.message});
      return;
    }

    let newReplies = response.replies;
    let more = response.more;

    let profiles = this.getProfilesInReplies(newReplies);
    await Server.public.loadProfiles(profiles);

    let replies = this.addNewRepliesToExistingReplies(newReplies, this.state.replies, this.state.sort);

    await Server.activity.setPostRepliesInCache(this.state.post.id, this.state.sort, {replies, more});

    this.setState({replies, more, loadingMore: false});
  }

  getProfilesInReplies(replies:any[]) {
    let profiles = [];
    for (let i = 0; i < replies.length; i++) {
      let reply = replies[i];
      if(profiles.indexOf(reply.user) == -1)
        profiles.push(reply.user);
    }
    return profiles;
  }

  addNewRepliesToExistingReplies(newReplies:any[], existingReplies:any[], sort:boolean) {
    let replies = JSON.parse(JSON.stringify(existingReplies));
    for(let i = 0; i < newReplies.length; i++) {
      let exists = false;
      for(let j = 0; j < replies.length; j++) 
        if(newReplies[i].date == replies[j].date && newReplies[i].user == replies[j].user)
          exists = true;
      if(!exists)
        replies.push(newReplies[i]);
    }
    this.sortReplies(replies, sort);
    return replies;
  }

  addRepliesToState(newReplies:any[], sort:boolean) {
    let replies = [...this.state.replies];
    
    for (let newReply of newReplies) {
      let existingIndex = replies.findIndex(reply => 
        reply.user === newReply.user && reply.date === newReply.date
      );
      
      if (existingIndex !== -1) {
        replies[existingIndex] = newReply;
      } else {
        replies.push(newReply);
      }
    }

    this.sortReplies(replies, sort);

    this.setState({replies});
  }

  sortReplies(replies:any[], sort:boolean) {
    replies.sort((a:any, b:any)=>{
      if(a.date > b.date)
        return sort ? 1 : -1;
      else if(a.date < b.date)
        return sort ? -1 : 1;
      return 0;
    })
  }

  async onPostReply(content:string) {
    let now = new Date().toISOString();

    let newReply = {
      user: Server.user.getId(),
      content: content,
      date: now
    };

    this.state.post.replies++;
    this.addRepliesToState([newReply], this.state.sort);

    if(this.state.sort)
      this.onJumpToBottom();

    let response = await Server.activity.createReply({
      id: this.state.post.id,
      content: content
    });
  
    if(response.success) {
      newReply.date = response.date;
      await Server.activity.updatePostInCaches(this.state.post);
      await Server.activity.clearPostRepliesCache(this.state.post.id);
    }
    else {
      this.state.post.replies--;
      let replies = this.state.replies.filter(reply => reply !== newReply);
      this.setState({replies, alert: response.message})
    }
  }

  onEditContent() {
    this.setState({editContent: true});
  }

  async onSaveContent(content:string) {
    this.setState({editContent: false});

    if(content == null)
      return;

    if(content.length == 0) {
      this.onDeletePost();
      return;
    }

    if(content.length > 1024) {
      this.setState({alert: 'Post must be less than 1024 characters'});
      return;
    }

    let oldContent = this.state.post.content;
    this.state.post.content = content;
    this.forceUpdate();

    let params:any = {
      id: this.state.post.id, 
      date: this.state.post.date, 
      content
    };

    let response = await Server.activity.editPost(params);

    if(response.success) {
      await Server.activity.updatePostInCaches(this.state.post);
    }
    else {
      this.state.post.content = oldContent;
      this.setState({alert: response.message});
    }
  }

  onEditReply(date:string) {
    this.setState({editReply: date});
  }

  async onSaveReply(content:string) {
    this.setState({editReply: null});

    if(content == null)
      return;

    for(let i = 0; i < this.state.replies.length; i++) {
      if(this.state.replies[i].date == this.state.editReply) {
        if(content == '')
          this.state.replies.splice(i, 1);
        else
          this.state.replies[i].content = content;
        this.forceUpdate();
        break;
      }
    }

    let response = null;

    if(content == '') {
      this.state.post.replies--;
      response = await Server.activity.deleteReply({
        id: this.state.post.id, 
        date: this.state.editReply
      });
    }
    else {
      response = await Server.activity.editReply({
        id: this.state.post.id,
        date: this.state.editReply,
        content: content
      });
    }

    if(response.success) {
      if(content == '') 
        await Server.activity.updatePostInCaches(this.state.post);
      await Server.activity.clearPostRepliesCache(this.state.post.id);
    }
    else  {
      if(content == '')
        this.state.post.replies++;
      this.setState({alert: response.message})
    }
  }

  onDeletePost() {
    this.setState({
      question: 'Do you want to delete this post and all its replies?', 
      onQuestionYes: ()=>this.onDeletePostYes()
    });
  }

  onDeletePostYes() {
    this.setState({question: '', message: 'Deleting post...'});
    setTimeout(async () => {
      let response = await Server.activity.deletePost(this.state.post.id);
      if(response.success) {
        await Server.activity.clearPostsCache();
        await Server.activity.clearPostInCaches(this.state.post.id);
        this.setState({message: ''});
        if(window.history.state && window.history.state.idx == 0)
          this.setState({navigate: '/activity'});
        else
          window.history.back();
      }
      else
        this.setState({alert: response.message, message: ''});
    }, 500);
  }

  onJumpToBottom() {
    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
  }

  async onSort() {
    if(this.state.more) 
      this.loadReplies(this.state.post.id, !this.state.sort);
    else 
      this.sortReplies(this.state.replies, !this.state.sort);

    this.setState({sort: !this.state.sort});
  }

  onJumpToReplies() {
    document.getElementById('RepliesHeader')?.scrollIntoView({behavior: 'smooth', block: 'center'});
  }

  async onLoadMore() {
    await this.loadMoreReplies();
  }

  onConvertToForumPost() {
    this.setState({ showConvertModal: true });
  }

  onCloseConvertModal() {
    this.setState({ showConvertModal: false });
  }

  async onConvertToForum(forumId: string) {
    this.setState({showConvertModal: false});

    if(this.state.more) {
      this.setState({message: 'Converting...'});
      await Server.public.sleep(250)
    }

    while(this.state.more) {
      await this.loadMoreReplies();
      await Server.public.sleep(250)
    }

    this.setState({message: ''})

    let content = decodeURIComponent(this.state.post.content);  
    for(let i = 0; i < this.state.replies.length; i++) 
      content += '<br/>' + decodeURIComponent(this.state.replies[i].content);

    while(content.indexOf('https://s3.us-west-2.amazonaws.com/oneclick.media') !== -1) 
      content = content.replace('https://s3.us-west-2.amazonaws.com/oneclick.media', 'https://d27jhqexqrz634.cloudfront.net');

    let post = {id: this.state.post.id, content};
    window.sessionStorage.setItem('ConvertActivityPostToForumTopic', JSON.stringify(post));
    
    this.setState({navigate: `/forum/${forumId}/new`});
  }

  renderLoading() {
    return (
      <div className="site-page-column">
        <PageHeader text={this.state.loading ? 'Loading...' : 'View Post'} navigate="/activity"/>

        {this.state.missing &&
          <div className="site-page-panel">
            This post has been deleted.
          </div>
        }
      </div>
    )
  }

  renderRepliesHeader() {
    let label = (this.state.post.replies > 1) ? `${this.state.post.replies} Replies` : '1 Reply';
    let headerId = 'RepliesHeader';
    
    return (
      <div className="site-page-header" id={headerId}>
        <div className="site-page-row" style={{justifyContent: 'space-between'}}>
          <div>
            {label}
          </div>
          <div style={{display: 'flex', flexDirection: 'row', columnGap: '15px'}}>
            <div style={{cursor: 'pointer'}} onClick={()=>this.onJumpToBottom()}>
              Bottom&nbsp;<HiArrowUturnDown style={{fontSize: '0.9em', transform: 'translateY(2px)'}} />
            </div>
            <div style={{cursor: 'pointer'}} onClick={()=>this.onSort()}>
              Sort&nbsp;
              {this.state.sort ?
                <BsCaretUpFill style={{fontSize: '1.0em', transform: 'translateY(3px)'}} /> :
                <BsCaretDownFill style={{fontSize: '1.0em', transform: 'translateY(3px)'}} />
              }
            </div>
          </div>
        </div>
      </div>
    )
  }

  renderContent() {
    let replyDivs = [];
    let replies = this.state.replies;
    
    for (let i = 0; i < replies.length; i++) {
      if(this.state.editReply == replies[i].date) {
        replyDivs.push(
          <PostPanel 
            key={i}
            placeholder="Enter reply..."
            edit={replies[i].content}
            onPost={(content:string)=>this.onSaveReply(content)}
            maxLength={1024}
          />
        )
      }
      else {
        let action = replies[i].user == Server.user.getId() && !this.state.post.converted ? 'edit' : '';
        replyDivs.push(
          <ContentPanel 
            key={i}
            author={replies[i].user}
            date={replies[i].date}
            content={replies[i].content}
            reply={true}
            action={action}
            onAction={()=>this.onEditReply(replies[i].date)}
          />
        )
      }
    }

    let action = this.state.post.author == Server.user.getId() && !this.state.post.converted ? 'edit' : '';

    let convertedDiv = this.state.post.converted ? 
      <div className="site-page-notification-panel">
        This post has been converted to a <NavLink to={`/forum/${this.state.post.converted.forum}/${this.state.post.converted.topic}`}>forum topic</NavLink>.
      </div> : null;

    return (
      <div className="site-page-column">
        <PageHeader text="View Post" navigate="/activity"> 
          {replies.length > 0 &&
            <div style={{display: 'flex', cursor: 'pointer'}} onClick={()=>this.onJumpToReplies()}>
              Replies&nbsp;<HiArrowUturnDown style={{fontSize: '0.9em', transform: 'translateY(4px)'}} />
            </div>
          }
        </PageHeader>

        {this.state.editContent &&
          <PostPanel 
            placeholder="Enter content..."
            edit={this.state.post.content}
            onPost={(content:string)=>this.onSaveContent(content)}
          />
        }

        {!this.state.editContent &&
          <ContentPanel 
            author={this.state.post.author}
            date={this.state.post.date}
            content={this.state.post.content}
            action={action}
            onAction={()=>this.onEditContent()}
            onConvert={this.state.post.converted == null ? ()=>this.onConvertToForumPost() : null}
          >
            <ActivityButtons 
              likes={this.state.post.likes} 
              liked={this.state.post.liked} 
              post={this.state.post} 
            />
          </ContentPanel>
        }

        {!this.state.editContent && this.state.post.converted == null &&
          <PostPanel 
            placeholder="Enter reply..."
            onPost={(content:string)=>this.onPostReply(content)}
            maxLength={1024}
          />
        }

        {!this.state.editContent && this.state.post.converted != null &&
          convertedDiv
        }

        {replies.length > 0 &&
          this.renderRepliesHeader()
        }

        {replyDivs}

        {this.state.more &&
          <button onClick={()=>this.onLoadMore()} style={{backgroundColor: 'var(--page-background-color)', border: '1px solid var(--panel-background-color)'}}>
            {this.state.loadingMore ? 'Loading...' : 'Load More Replies'}
          </button>
        }

        {replies.length > 2 && !this.state.editContent && this.state.post.converted == null &&
          <PostPanel 
            placeholder="Enter reply..."
            onPost={(content:string)=>this.onPostReply(content)}
            maxLength={1024}
          />
        }
      </div>
    )
  }

  render() {
    if(this.state.navigate != '')
      return (<Navigate to={this.state.navigate} />);
      
    return (
      <div className="site-page">
        <PageEnvelope width="600px">
          {this.state.loading || this.state.missing ? this.renderLoading() : this.renderContent()}
        </PageEnvelope>

        <MessageModal message={this.state.message} />
        <AlertModal message={this.state.alert} button="OK" onClose={()=>this.setState({alert: ''})}/>
        <QuestionModal message={this.state.question} onYes={()=>this.state.onQuestionYes()} onNo={()=>this.setState({question: ''})} />
        {this.state.showConvertModal && 
          <ConvertToForumModal 
            onClose={() => this.onCloseConvertModal()}
            onConvert={(forumId: string) => this.onConvertToForum(forumId)}
          />
        }
      </div>
    );
  }
}

export default ActivityPostPage;